import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import { useAuthContext } from './AuthContext';

type OrganizationState = {
  // No consumer reads this: the effect that fills it is the dead reader in
  // KNOWN_BUGS.md SH-2.
  eligibilityGroups: string[] | null;
  isEligibilityGroupsLoading: boolean;
  payersOptions: { key: string; text: string; value: string }[];
  selectedPayor: string | null;
  setIsEligibilityGroupsLoading: (isLoading: boolean) => void;
  setEligibilityGroups: (eligibilityGroups: string[] | null) => void;
  setSelectedPayor: (selectedPayor: string | null) => void;
};

const initialOrganizationState: OrganizationState = {
  eligibilityGroups: [],
  isEligibilityGroupsLoading: false,
  payersOptions: [],
  selectedPayor: null,
  setIsEligibilityGroupsLoading: () => {},
  setEligibilityGroups: () => {},
  setSelectedPayor: () => {},
};

export const OrganizationContext = createContext<OrganizationState | undefined>(undefined);

/**
 * Read the organization context, narrowed to `OrganizationState`.
 *
 * A CAST, not a guard — the same treatment as `useAuthContext` (§2.10 F3). Upstream consumers
 * destructure `useContext(OrganizationContext)` directly, so an absent provider throws a
 * TypeError there. An explicit `throw` emits, and it replaces that TypeError with a different
 * error carrying a different message — observable to any caller that inspects it, and a runtime
 * change rather than a narrowing.
 *
 * Consumers do render beneath an `OrganizationProvider`, so the `undefined` default is
 * unreachable either way. The cast keeps it that way without emitting.
 */
export function useOrganizationContext(): OrganizationState {
  return useContext(OrganizationContext) as OrganizationState;
}

export const OrganizationProvider = ({ children }: { children: ReactNode }) => {
  const { eligibilityGroups, payers } = useAuthContext();
  const payersOptions = [{ key: 'ALL', text: 'ALL', value: 'ALL' }].concat(payers); //for fan search

  const [organizations, setOrganizations] = useState<OrganizationState>(initialOrganizationState);
  const setIsEligibilityGroupsLoading = (isLoading: boolean) => {
    setOrganizations((prevState) => ({
      ...prevState,
      isEligibilityGroupsLoading: isLoading,
    }));
  };

  const setSelectedEligibilityGroup = (selectedEligibilityGroup: string | null) => {
    setOrganizations((prevState) => ({
      ...prevState,
      selectedEligibilityGroup,
    }));
  };
  const setSelectedPayor = (selectedPayor: string | null) => {
    setOrganizations((prevState) => ({
      ...prevState,
      selectedPayor,
    }));
  };
  const updateEligibilityGroups = (eligibilityGroups: string[] | null) => {
    setOrganizations((prevState) => ({
      ...prevState,
      eligibilityGroups,
    }));
  };

  useEffect(() => {
    //if payor is updated, eligibilityGroups dropdown is updated as well
    const { selectedPayor } = organizations;
    if ((eligibilityGroups?.length ?? 0) > 0 && selectedPayor && selectedPayor !== 'ALL') {
      const selectedPayorEligibilityGroup = eligibilityGroups?.find(
        (eligibilityGroup) => eligibilityGroup.orgId === selectedPayor,
      )?.eligiblityGroups;
      if (selectedPayorEligibilityGroup && selectedPayorEligibilityGroup?.length > 0) {
        updateEligibilityGroups(selectedPayorEligibilityGroup);
        setSelectedEligibilityGroup(selectedPayorEligibilityGroup[0]!);
      } else {
        updateEligibilityGroups(null);
        setSelectedEligibilityGroup(null);
      }
    } else {
      updateEligibilityGroups(null);
      setSelectedEligibilityGroup(null);
    }
  }, [eligibilityGroups, organizations.selectedPayor]);
  return (
    <OrganizationContext.Provider
      value={{
        ...organizations,
        payersOptions,
        setEligibilityGroups: updateEligibilityGroups,
        setIsEligibilityGroupsLoading,
        setSelectedPayor,
      }}
    >
      {children}
    </OrganizationContext.Provider>
  );
};
